import React from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SelectField from '@/components/common/SelectField';
import { UPLOAD_CRS_GROUPS } from '@/config/upload';
import { needsCrs } from '@/utils/upload';
import { UploadPreviewSheet } from '@/types/upload';

interface UploadLayerInfoProps {
  /** A layer of a file read through GDAL (shapefile, GeoPackage, KML): it says its geometry, coordinate system, encoding. */
  sheet: UploadPreviewSheet;
  crs: string;
  disabled: boolean;
  onCrs: (crs: string) => void;
}

/**
 * What a shapefile's (or GeoPackage's, KML's) layer is, before it is imported: its kind of geometry, what of its
 * coordinates is dropped (heights, measures), its coordinate system - or, when the file does not say one (no .prj),
 * the choice of it, starting from what the coordinates look like - and how its texts are read when the file did not
 * say (no .cpg), so the person checks the sample values.
 */
const UploadLayerInfo: React.FC<UploadLayerInfoProps> = ({ sheet, crs, disabled, onCrs }) => {
  const { t } = useTranslation();
  const lines: React.ReactNode[] = [];
  if (sheet.geometryType) {
    lines.push(t('uploads.file_geometry', { kind: t(`uploads.geometry_${sheet.geometryType}`, { defaultValue: sheet.geometryType }) })
      + (sheet.sourceGeometryType ? ` (${sheet.sourceGeometryType})` : ''));
  }
  if (sheet.droppedDimensions) {
    lines.push(t(`uploadShp.dropped_${sheet.droppedDimensions.toLowerCase()}`));
  }
  if (sheet.crsKnown) {
    const name = [sheet.crsName, sheet.crs].filter(Boolean).join(' · ');
    lines.push(t('uploadShp.crs_known', { name: name || t('uploadShp.crs_unnamed') }));
  }
  return (
    <div className="space-y-2">
      {lines.length > 0 && (
        <ul className="text-xs text-gn-muted space-y-0.5">
          {lines.map((l, i) => (
            <li key={i} className="flex items-start gap-1.5"><Info size={12} className="mt-0.5 shrink-0" />{l}</li>
          ))}
        </ul>
      )}
      {needsCrs(sheet) && (
        <div className="space-y-2 border border-gn-light rounded-md p-2">
          <p className="text-xs text-gn-warning flex items-start gap-1.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            <span>
              {t('uploadShp.crs_unknown')}
              {sheet.suggestedCrs ? ` ${t('uploadShp.crs_guess', { crs: sheet.suggestedCrs })}` : ''}
            </span>
          </p>
          <SelectField label={t('uploads.crs')} value={crs} disabled={disabled} onChange={(e) => onCrs(e.target.value)}
                       className="sm:max-w-sm">
            {UPLOAD_CRS_GROUPS.map((g) => (
              <optgroup key={g.key} label={t(`uploads.crs_group_${g.key}`)}>
                {g.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </optgroup>
            ))}
          </SelectField>
        </div>
      )}
      {sheet.encodingGuessed && sheet.encoding && (
        <p className="text-xs text-gn-warning flex items-start gap-1.5">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          {t('uploadShp.encoding_guessed', { encoding: sheet.encoding })}
        </p>
      )}
    </div>
  );
};

export default UploadLayerInfo;
